import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Download, RefreshCcw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, download, requestWithMessage } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { usePeriod } from '../lib/period';
import type { AiView, Confidence } from '../lib/types';
import { Badge, Button, Card, ErrorState, LoadingBlock, Modal, PageHeader, Pagination, Table, Td, Th, Tr, useToast, type BadgeTone } from '../components/ui';
import { AiPanel, NotMeasurable, Section, SnapshotStatus, useNumber } from '../components/kit';
import { DayRange, Fact, hrefWith, lastDayOf, periodQueryOf } from '../components/a-common';
import { FinanceTrust, rowsFromMonths } from '../components/a-finance';
import { MonthTrend } from '../components/a-charts';
import { AiActions, CoverageSummary, WrittenForNote, useKindText, type ReportCoverage } from '../components/a-ai';
import { evidenceHref, HealthCard, OverviewKpis, ScoreLabelBadge, SignalsCard, UrgentSection, WorkSummaryCard, type OverviewData } from '../components/a-overview';

/**
 * One executive report (CR-008 §13): a stored snapshot of a period — its status, revision, time zone and
 * the settings it was scored with, every revision of it, the figures exactly as stored (the Overview's own
 * blocks), the people in it with names read now (a snapshot keeps ids, never names), AI's explanation, and
 * its CSV and PDF exports in the reader's language.
 */

interface ReportDetail {
  id: string;
  kind: string;
  periodStart: string;
  periodEnd: string;
  timezone: string;
  status: string;
  revision: number;
  isCurrent: boolean;
  generatedBy: string;
  requestedBy: { id: string; fullName: string; fullNameAr: string | null } | null;
  computedAt: string;
  finalizedAt: string | null;
  definitionVersion: string;
  settingsVersion: number;
  coverage: ReportCoverage | null;
  data: OverviewData | null;
  people: Array<{ id: string; fullName: string | null; fullNameAr: string | null; jobTitle: string | null }>;
  departments: Array<{ id: string; name: string; nameAr: string | null }>;
  ai: AiView;
  revisions: Array<{ id: string; revision: number; status: string; isCurrent: boolean; computedAt: string; finalizedAt: string | null; revisionReason: string | null }>;
}

const PEOPLE_PAGE = 25;
const CONFIDENCE_TONE: Record<string, BadgeTone> = { HIGH: 'success', MEDIUM: 'info', LOW: 'warning', NONE: 'neutral' };

/** The stored figures can be read with the Overview's blocks only in the shape those blocks know. */
function readable(data: OverviewData | null | undefined): data is OverviewData {
  return !!data && !!data.period?.comparison && !!data.finance?.cards && !!data.finance.context && !!data.health && !!data.work && !!data.people?.summary;
}

export function ReportDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { t, locale, has, localized } = useI18n();
  const { query } = usePeriod();
  const kindText = useKindText();
  const q = useQuery({
    queryKey: ['executive', 'report', id],
    queryFn: () => api.get<ReportDetail>(`/executive/reports/${id}`),
    enabled: !!id,
    // An explanation being written is looked at again until it is ready — read from the database, never asked for.
    refetchInterval: (q) => (['PENDING', 'RUNNING'].includes(q.state.data?.ai?.status ?? '') ? 10_000 : false),
  });
  const queryClient = useQueryClient();
  const refreshAi = () => {
    void queryClient.invalidateQueries({ queryKey: ['executive', 'report', id] });
    void queryClient.invalidateQueries({ queryKey: ['executive', 'reports'] });
    void queryClient.invalidateQueries({ queryKey: ['executive', 'insights'] });
  };
  const r = q.data;
  const back = (
    <Link to={hrefWith('/reports', query)} className="mb-3 inline-flex items-center gap-1.5 text-body-sm text-[var(--text-secondary)] hover:text-primary">
      <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
      {t('reports.back')}
    </Link>
  );

  if (q.isLoading) {
    return (
      <>
        {back}
        <LoadingBlock />
      </>
    );
  }
  if (q.error || !r) {
    return (
      <>
        {back}
        <Card>
          <ErrorState error={q.error ?? t('common.error')} onRetry={() => void q.refetch()} />
        </Card>
      </>
    );
  }

  const reportQuery = periodQueryOf(r.kind, r.periodStart, r.periodEnd);
  const last = lastDayOf(r.periodEnd);
  const current = r.revisions.find((v) => v.isCurrent && v.id !== r.id);
  const data = readable(r.data) ? r.data : null;
  const generatedBy = (() => {
    const key = `reports.generatedBy.${r.generatedBy}`;
    return has(key) ? t(key) : r.generatedBy;
  })();
  const reason = (code: string | null) => {
    if (!code) return null;
    const key = `reports.detail.reason.${code}`;
    return has(key) ? t(key) : code;
  };

  return (
    <>
      {back}
      <PageHeader
        title={t('reports.detail.title', { kind: kindText(r.kind) })}
        subtitle={<DayRange start={r.periodStart} last={last} />}
        action={
          <>
            {r.isCurrent && <RecalculateButton id={r.id} query={query} />}
            <ExportButton path={`/executive/reports/${r.id}/export.csv`} label={t('common.exportCsv')} />
            <ExportButton path={`/executive/reports/${r.id}/export.pdf`} label={t('common.exportPdf')} />
          </>
        }
      />

      <div className="space-y-6">
        <Card>
          <div className="flex flex-wrap items-center gap-2">
            <SnapshotStatus status={r.status} />
            <Badge tone="neutral">{t('reports.revisionShort', { n: r.revision })}</Badge>
            {!r.isCurrent && <Badge tone="warning">{t('reports.detail.superseded')}</Badge>}
          </div>
          {!r.isCurrent && current && (
            <p className="mt-2 flex items-center gap-2 text-body-sm text-warning-fg dark:text-warning-dark">
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
              {t('reports.detail.notCurrent')}{' '}
              <Link to={hrefWith(`/reports/${current.id}`, query)} className="underline">
                {t('reports.detail.openCurrent', { n: current.revision })}
              </Link>
            </p>
          )}
          <dl className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Fact label={t('reports.detail.period')}>
              <DayRange start={r.periodStart} last={last} />
            </Fact>
            <Fact label={t('reports.detail.timezone')}>{r.timezone}</Fact>
            <Fact label={t('reports.detail.generatedBy')}>
              {generatedBy}
              {r.requestedBy && <span className="block text-caption text-muted">{t('reports.detail.requestedBy', { name: localized(r.requestedBy) })}</span>}
            </Fact>
            <Fact label={t('reports.detail.versions')}>
              {t('reports.detail.settingsVersion', { v: r.settingsVersion })}
              <span className="block font-mono text-mono-sm text-muted" dir="ltr">
                {r.definitionVersion}
              </span>
            </Fact>
            <Fact label={t('reports.col.computed')}>{formatDateTime(r.computedAt, locale)}</Fact>
            <Fact label={t('reports.col.finalized')}>{r.finalizedAt ? formatDateTime(r.finalizedAt, locale) : t('reports.notFinalized')}</Fact>
            <Fact label={t('reports.col.coverage')} className="sm:col-span-2">
              <CoverageSummary coverage={r.coverage} />
            </Fact>
          </dl>
          {data?.period.live && <p className="mt-3 text-caption text-muted">{t('reports.detail.liveWhenComputed', { time: formatDateTime(data.asOf, locale) })}</p>}
          <p className="mt-3 text-caption text-[var(--text-tertiary)]">{t('reports.detail.storedNote')}</p>
        </Card>

        {r.revisions.length > 1 && (
          <Section title={t('reports.detail.revisions')}>
            <ul className="divide-y divide-[var(--line)]">
              {r.revisions.map((v) => (
                <li key={v.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-body-sm">
                  <span className="inline-flex flex-wrap items-center gap-2">
                    {v.id === r.id ? (
                      <span className="font-medium">{t('reports.revisionShort', { n: v.revision })}</span>
                    ) : (
                      <Link to={hrefWith(`/reports/${v.id}`, query)} className="text-primary-600 hover:underline dark:text-primary-400">
                        {t('reports.revisionShort', { n: v.revision })}
                      </Link>
                    )}
                    <SnapshotStatus status={v.status} />
                    {v.isCurrent && <Badge tone="success">{t('reports.detail.current')}</Badge>}
                    {v.id === r.id && <Badge tone="neutral">{t('reports.detail.viewing')}</Badge>}
                  </span>
                  <span className="text-caption text-muted">
                    {formatDateTime(v.computedAt, locale)}
                    {v.revisionReason && <> · {reason(v.revisionReason)}</>}
                  </span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {!data ? (
          <Card>
            <p className="text-body-sm text-muted">{t('reports.detail.unreadable')}</p>
          </Card>
        ) : (
          <>
            <section aria-labelledby="figures-title" className="space-y-4">
              <div>
                <h2 id="figures-title" className="text-h2 text-[var(--text-primary)]">
                  {t('reports.detail.figures')}
                </h2>
                <p className="mt-1 text-caption text-muted">{t('reports.detail.figuresHint', { time: formatDateTime(data.asOf, locale) })}</p>
              </div>
              <Card>
                <FinanceTrust context={data.finance.context} />
              </Card>
              <OverviewKpis data={data} query={reportQuery} />
            </section>

            <div className="grid gap-4 xl:grid-cols-3">
              <HealthCard health={data.health} />
              <div className="xl:col-span-2">
                <div className="space-y-2">
                  <AiPanel
                    ai={r.ai}
                    title={t('reports.detail.ai')}
                    evidenceLink={(eid) => evidenceHref(eid, reportQuery)}
                    action={r.ai && r.isCurrent ? <AiActions ai={r.ai} askPath={`/executive/reports/${r.id}/explanation`} onChanged={refreshAi} /> : undefined}
                  />
                  <WrittenForNote ai={r.ai} revision={r.revision} current={r.isCurrent} />
                  <p className="text-caption text-muted">{t('aiAct.savedFirst')}</p>
                </div>
              </div>
            </div>

            <SignalsCard risks={data.risks ?? []} positives={data.positives ?? []} currency={data.finance.context.baseCurrency} query={reportQuery} />

            <div className="grid gap-4 xl:grid-cols-2">
              <Section title={t('reports.detail.months')} subtitle={t('finance.inBase', { currency: data.finance.context.baseCurrency })}>
                <MonthTrend rows={rowsFromMonths(data.finance.months)} currency={data.finance.context.baseCurrency} />
              </Section>
              <WorkSummaryCard work={data.work} previous={data.workPrevious} basis={data.period.comparison.basis} query={reportQuery} />
            </div>

            <PeopleSection report={r} data={data} reportQuery={reportQuery} />

            <div>
              <p className="mb-2 text-caption text-muted">{t('reports.detail.urgentAsComputed', { time: formatDateTime(data.asOf, locale) })}</p>
              <UrgentSection urgent={data.urgent} query={reportQuery} />
            </div>
          </>
        )}
      </div>
    </>
  );
}

/**
 * Recalculate — the one way a saved report is read again (the operator's CR, 2026-11-04): asked for explicitly,
 * after a confirmation. A new revision only if the period's records changed — opened at once; the older one kept.
 */
function RecalculateButton({ id, query }: { id: string; query: string }) {
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const recalc = useMutation({
    mutationFn: () => requestWithMessage<{ id: string; revision: number; status: string; changed: boolean }>(`/executive/reports/${id}/recalculate`, { method: 'POST' }),
    onSuccess: ({ data }) => {
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['executive', 'reports'] });
      void queryClient.invalidateQueries({ queryKey: ['executive', 'report'] });
      if (data.changed) {
        toast.success(t('reports.recalculated', { revision: data.revision }));
        navigate(hrefWith(`/reports/${data.id}`, query));
      } else {
        toast.success(t('reports.recalculateUnchanged'));
      }
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : String(e)),
  });
  return (
    <>
      <Button icon={<RefreshCcw className="h-4 w-4" aria-hidden />} onClick={() => setOpen(true)}>
        {t('reports.recalculate')}
      </Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        size="sm"
        title={t('reports.recalculateTitle')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              {t('common.close')}
            </Button>
            <Button variant="primary" loading={recalc.isPending} onClick={() => recalc.mutate()}>
              {t('reports.recalculateConfirm')}
            </Button>
          </>
        }
      >
        <p className="text-body-sm text-[var(--text-secondary)]">{t('reports.recalculateBody')}</p>
      </Modal>
    </>
  );
}

/** An export of this report (CSV or PDF), in the reader's language — a file, with its failure said. */
function ExportButton({ path, label }: { path: string; label: string }) {
  const { t, locale } = useI18n();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  return (
    <Button
      icon={<Download className="h-4 w-4" aria-hidden />}
      loading={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await download(path, { locale });
        } catch (e) {
          toast.error(t('reports.exportFailed', { message: e instanceof Error ? e.message : String(e) }));
        } finally {
          setBusy(false);
        }
      }}
    >
      {label}
    </Button>
  );
}

/** The people in the report: names read now (a removed account is a former employee), never ranked. */
function PeopleSection({ report, data, reportQuery }: { report: ReportDetail; data: OverviewData; reportQuery: string }) {
  const { t, has, locale } = useI18n();
  const num = useNumber();
  const [page, setPage] = useState(1);
  const people = useMemo(() => new Map(report.people.map((p) => [p.id, p])), [report.people]);
  const departments = useMemo(() => new Map(report.departments.map((d) => [d.id, d])), [report.departments]);
  const rows = data.people.rows ?? [];
  const pageCount = Math.max(1, Math.ceil(rows.length / PEOPLE_PAGE));
  const shown = rows.slice((page - 1) * PEOPLE_PAGE, page * PEOPLE_PAGE);
  const summary = data.people.summary;
  const confidence = (c: Confidence) => {
    const key = `reports.confidence.${c}`;
    return has(key) ? t(key) : c;
  };
  const nameOf = (id: string) => {
    const p = people.get(id);
    if (!p || !p.fullName) return null;
    return locale === 'ar' ? p.fullNameAr || p.fullName : p.fullName;
  };
  return (
    <Section
      title={t('reports.detail.people')}
      subtitle={t('reports.detail.peopleHint', { people: num(summary.people), scored: num(summary.scored), none: num(summary.notEnoughEvidence) })}
    >
      {data.people.truncated && <p className="mb-2 text-caption text-warning-fg dark:text-warning-dark">{t('state.truncated')}</p>}
      {rows.length === 0 ? (
        <p className="text-body-sm text-muted">{t('reports.detail.noPeople')}</p>
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>{t('common.employee')}</Th>
                <Th>{t('common.department')}</Th>
                <Th>{t('reports.detail.score')}</Th>
                <Th>{t('reports.detail.confidence')}</Th>
                <Th align="end">{t('reports.detail.evidence')}</Th>
                <Th align="end">{t('reports.detail.measurableWeight')}</Th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => {
                const name = nameOf(row.userId);
                const depts = row.departmentIds
                  .map((d) => departments.get(d))
                  .filter((d): d is NonNullable<typeof d> => !!d)
                  .map((d) => (locale === 'ar' ? d.nameAr || d.name : d.name));
                return (
                  <Tr key={row.userId}>
                    <Td>
                      {name ? (
                        <Link to={hrefWith(`/employees/${row.userId}`, reportQuery)} className="text-primary-600 hover:underline dark:text-primary-400">
                          {name}
                        </Link>
                      ) : (
                        <span className="text-muted">{t('common.formerEmployee')}</span>
                      )}
                    </Td>
                    <Td className="text-caption text-muted">{depts.length ? depts.join(' · ') : '—'}</Td>
                    <Td>
                      {row.score === null ? (
                        <NotMeasurable compact reason={row.reason} />
                      ) : (
                        <span className="inline-flex items-center gap-2">
                          <span className="tabular font-medium">{num(row.score, 0)}</span>
                          <ScoreLabelBadge label={row.label} />
                        </span>
                      )}
                    </Td>
                    <Td>
                      <Badge tone={CONFIDENCE_TONE[row.confidence] ?? 'neutral'}>{confidence(row.confidence)}</Badge>
                    </Td>
                    <Td align="end" className="tabular">{num(row.evidenceCount)}</Td>
                    <Td align="end" className="tabular">{`${num(row.measurableWeight, 1)}%`}</Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
          <Pagination page={page} pageCount={pageCount} total={rows.length} onChange={setPage} />
        </>
      )}
      <p className="mt-3 text-caption text-muted">{t('reports.detail.peopleNote')}</p>
    </Section>
  );
}
